import React, { useState, useRef, useEffect } from "react";
import Draggable from "react-draggable";
import { PrimaryButton } from "./Buttons.jsx";

const BreakpointBar = ({
  setMileageBreakpoint,
  initBreakpoints,
  largest = 10,
}) => {
  const [breakpoints, setBreakpoints] = useState(initBreakpoints || []);
  const barRef = useRef(null);
  const [barWidth, setBarWidth] = useState(0);
  const dotColors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-red-500",
    "bg-indigo-500",
  ];

  const updateBarWidth = () => {
    if (barRef.current) {
      setBarWidth(barRef.current.clientWidth);
    }
  };

  useEffect(() => {
    setMileageBreakpoint(breakpoints);
  }, [breakpoints]);

  useEffect(() => {
    updateBarWidth();
    window.addEventListener("resize", updateBarWidth);
    return () => {
      window.removeEventListener("resize", updateBarWidth);
    };
  }, []);

  const addBreakpoint = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (breakpoints.length === 0) {
      setBreakpoints([5]);
    } else if (breakpoints.length === 1) {
      const firstBreakpoint = breakpoints[0];
      const midpoint =
        firstBreakpoint >= 5
          ? Math.floor(firstBreakpoint / 2)
          : Math.floor((largest + firstBreakpoint) / 2);
      setBreakpoints([firstBreakpoint, midpoint]);
    } else {
      const lastBreakpoint = breakpoints[breakpoints.length - 1];
      const midpoint = Math.floor((largest + lastBreakpoint) / 2);
      setBreakpoints([...breakpoints, midpoint]);
    }
  };

  const removeBreakpoint = (index) => {
    setBreakpoints((prevBreakpoints) =>
      prevBreakpoints.filter((_, i) => i !== index)
    );
  };

  const handleDrag = (e, data, index) => {
    const newBreakpoint = Math.round((data.x / barWidth) * largest);
    setBreakpoints((prevBreakpoints) => {
      const updatedBreakpoints = [...prevBreakpoints];
      const otherIndex = index === 0 ? 1 : 0;
      const otherBreakpoint = prevBreakpoints[otherIndex];
      const oldBreakpoint = prevBreakpoints[index];

      if (otherBreakpoint !== undefined) {
        if (oldBreakpoint < otherBreakpoint) {
          updatedBreakpoints[index] = Math.min(
            otherBreakpoint - 1,
            Math.max(1, newBreakpoint)
          );
        } else {
          updatedBreakpoints[index] = Math.min(
            largest-1,
            Math.max(newBreakpoint, otherBreakpoint + 1)
          );
        }
      } else {
        updatedBreakpoints[index] = Math.min(largest-1, Math.max(1, newBreakpoint));
      }

      return updatedBreakpoints;
    });
  };

  const getBounds = (index) => {
    const otherIndex = index === 0 ? 1 : 0;
    const otherBreakpoint = breakpoints[otherIndex];
    if (otherBreakpoint === undefined) {
      return { left: barWidth / largest, right: (barWidth / largest) * largest-1 };
    }
    if (breakpoints[index] < otherBreakpoint) {
      return {
        left: barWidth / largest,
        right: (otherBreakpoint / largest) * barWidth - barWidth / largest,
      };
    }
    return {
      left: (otherBreakpoint / largest) * barWidth + barWidth / largest,
      right: (barWidth / largest) * largest-1,
    };
  };

  return (
    <div
      className={
        "flex flex-col items-center w-full px-4 " +
        (breakpoints.length > 0 ? "pb-36" : "pb-8")
      }
    >
      <div className="flex items-center w-full">
        <div ref={barRef} className="relative flex-1 h-1 bg-gray-300 mr-4">
          <div className="absolute w-full flex justify-between top-[-4px]">
            {[...Array(largest + 1)].map((_, i) => (
              <div key={i} className="w-0.5 h-4 bg-black">
                <div className="absolute top-4 -translate-x-1">{i}</div>
              </div>
            ))}
          </div>

          {breakpoints.map((breakpoint, index) => {
            let bubblePos = "";
            if (breakpoints.length > 1) {
              const otherIndex = index === 0 ? 1 : 0;
              const otherBreakpoint = breakpoints[otherIndex];
              if (breakpoint < otherBreakpoint) {
                bubblePos = "left-bubble";
              } else {
                bubblePos = "right-bubble";
              }
            }
            return (
              <Draggable
                key={index}
                axis="x"
                bounds={getBounds(index)}
                grid={[barWidth / largest, 0]}
                position={{
                  x:
                    (Math.min(largest - 1, Math.max(1, breakpoint)) / largest) *
                      barWidth -
                    12,
                  y: 0,
                }}
                onDrag={(e, data) => handleDrag(e, data, index)}
              >
                <div className="absolute bottom-0 transform -translate-x-1/2">
                  <div className="relative flex flex-col items-center translate-y-[10px]">
                    <div
                      className={`w-6 h-6 ${dotColors[index]} opacity-70 rounded-full cursor-pointer`}
                    ></div>
                    <div
                      className={
                        "absolute top-8 flex flex-col items-center p-2 bg-white border border-gray-400 rounded speech-bubble " +
                        bubblePos
                      }
                    >
                      <span>{breakpoint} miles</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeBreakpoint(index);
                        }}
                        className="mt-2 px-2 py-1 bg-red-500 text-white rounded"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </Draggable>
            );
          })}
        </div>
        <div className="ml-8">
          <PrimaryButton
            onClick={addBreakpoint}
            disabled={breakpoints.length >= (largest > 11 ? 4 : 2)}
          >
            Add Breakpoint
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default BreakpointBar;
